import _ from "lodash";
import * as d3 from "d3";
import {
  CHART_FOCUS_POINT_RADIUS,
  GRANULARITY_DAY,
  GRANULARITY_WEEK,
  GRANULARITY_MONTH,
  GRANULARITY_QUARTER,
  GRANULARITY_YEAR,
  DATE_FORMAT,
  DATE_MONTH_YEAR_FORMAT,
  DATE_YEAR_FORMAT,
  API_KEY_TIMESTAMP,
  CHARTS
} from "../managers/constants";
import datetimeManager from "../managers/datetime";
import mathManager from "../managers/math";

const service = ((doc) => {
  let xAxisTickLabelPadding = 1;
  let yAxisTickLabelPadding = 1;

  // Generate a unique Id for the Drawing container element
  const getDrawingContainerElementId = (whiteboardIndex, drawingIndex, drawingIdentifier) =>
    "drawingContainer__" + whiteboardIndex + "__" + drawingIndex + "__" + drawingIdentifier;

  // Remove drawing from DOM and memory
  const destroyDrawing = (drawingContainerElementId, destroySelf = true) => {
    let n = doc.getElementById(drawingContainerElementId);
    if (n) {
      while (n.firstChild) {
        n.removeChild(n.firstChild);
      }
      if (destroySelf) n.parentNode.removeChild(n);
    }
  };

  // Get date format for X axis
  const getXAxisDateFormat = (granularity) => {
    if (granularity === GRANULARITY_DAY || granularity === GRANULARITY_WEEK) {
      return DATE_FORMAT;
    } else if (granularity === GRANULARITY_MONTH || granularity === GRANULARITY_QUARTER) {
      return DATE_MONTH_YEAR_FORMAT;
    } else if (granularity === GRANULARITY_YEAR) {
      return DATE_YEAR_FORMAT;
    }
  };

  // Get percentage change between old and new value
  const getPercentageChange = (oldValue, newValue, granularity, inversePercentageIndication = false) => {
    if (!_.isNumber(oldValue) || !_.isNumber(newValue)) return "";

    let percentageChange = mathManager.calculatePercentChange(oldValue, newValue);

    // inversePercentageIndication : this variable is used to inverse color indication for 
    // granularity percentage diff(DoD% / WoW% / MoM% etc.) in the tooltip
    let spanClass = percentageChange > 0 ?
      inversePercentageIndication ? "negative" : "positive" :
      inversePercentageIndication ? "positive" : "negative";
    let caretClass = percentageChange > 0 ? "fa-caret-up" : "fa-caret-down";
    granularity = granularity.charAt(0);

    return (
      "<span class='" +
      spanClass +
      "'><i class='fa " +
      caretClass +
      "' aria-hidden='true'></i> " +
      Math.abs(percentageChange) +
      "</span> <span>" +
      granularity +
      "o" +
      granularity +
      "%</span>"
    );
  };

  // Create the SVG element
  const createSvg = (drawingContainerElementId, width, height, margin) =>
    d3
      .select("#" + drawingContainerElementId)
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("class", "chart-svg")
      .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

  // Create timescale for X axis
  const createXAxisTimeScale = (x, tickValues, tickFormat) =>
    d3
      .axisBottom(x)
      .tickValues(tickValues)
      .tickFormat(d3.timeFormat(tickFormat))
      .tickPadding(xAxisTickLabelPadding);

  // Create linear scale for Y axis
  const createYAxisLinearScale = (y) =>
    d3
      .axisLeft(y)
      .tickPadding(yAxisTickLabelPadding)
      .tickFormat(function(d) {
        // Show negative as positive
        return mathManager.toUSANumberFormat(Math.abs(d));
      });

  // Create linear scale for Dual-Y axis
  const createDualYAxisLinearScale = (dualY) =>
    d3
      .axisRight(dualY)
      .tickPadding(yAxisTickLabelPadding)
      .tickFormat(function(d) {
        // Show negative as positive
        return mathManager.toUSANumberFormat(Math.abs(d));
      });

  // Draw X axis
  const drawXAxis = (svg, height, xAxis) => {
    svg
      .append("g")
      .attr("class", "axis")
      .call(xAxis)
      .attr("transform", "translate(0," + height + ")");
  };

  // Draw Y axis
  const drawYAxis = (svg, yAxis) => {
    svg
      .append("g")
      .attr("class", "axis")
      .call(yAxis)
      .append("text");
  };

  // Draw Dual-Y axis
  const drawDualYAxis = (svg, width, dualYAxis) => {
    svg
      .append("g")
      .attr("class", "axis")
      .call(dualYAxis)
      .attr("transform", "translate(" + width + ")", 0)
      .append("text");
  };

  // Draw X axis label
  const drawXAxisLabel = (svg, width, height, xAxisLabel) =>
    svg
      .append("text")
      .attr("class", "axis axis-label")
      .attr("transform", "translate(" + width / 2 + " ," + (height + 35) + ")")
      .text(xAxisLabel);

  // Draw Y axis label
  const drawYAxisLabel = (svg, height, yAxisLabel, margin) => {
    let yAxisLabelDx = "0px"; // Vertically
    let yAxisLabelDy = -margin.left + 8 + "px"; // Horizontally
    return svg
      .append("text")
      .attr("class", "axis axis-label")
      .attr("transform", "rotate(-90)")
      .attr("x", 0 - height / 2)
      .attr("y", 0)
      .attr("dx", yAxisLabelDx)
      .attr("dy", yAxisLabelDy)
      .text(yAxisLabel);
  };

  // Draw Dual-Y axis label
  const drawYDualAxisLabel = (svg, width, height, dualYAxisLabel, margin) => {
    let dualYAxisLabelDx = "0px";
    let dualYAxisLabelDy = margin.right + "px";
    return svg
      .append("text")
      .attr("class", "axis axis-label")
      .attr("transform", "rotate(-90)")
      .attr("x", 0 - height / 2)
      .attr("y", width)
      .attr("dx", dualYAxisLabelDx)
      .attr("dy", dualYAxisLabelDy)
      .text(dualYAxisLabel);
  };

  // Draw chart data tooltip
  const drawChartDataTooltip = (drawingContainerElementId, uniqueKey) =>
    d3
      .select("#" + drawingContainerElementId)
      .append("div")
      .attr("id", "chartTooltip" + uniqueKey)
      .attr("class", "chart-tooltip chart-tooltip-data");

  // Position chart data tooltip
  const positionChartDataTooltip = (center, xMousePosition, tooltip, tooltipContent, margin) => {
    if (center >= xMousePosition) {
      tooltip
        .html("<table>" + tooltipContent)
        .style("display", "block")
        .style("left", "auto")
        .style("right", margin.right + 5 + "px") + "</table>";
    } else {
      tooltip
        .html("<table>" + tooltipContent)
        .style("display", "block")
        .style("right", "auto")
        .style("left", margin.left + 5 + "px") + "</table>";
    }
  };

  // Draw tooltip on Y axis
  const drawChartTooltipYAxis = (drawingContainerElementId, uniqueKey, id, margin, width) =>
    d3
      .select("#" + drawingContainerElementId)
      .append("div")
      .attr("id", id + "" + uniqueKey)
      .attr("class", "chart-tooltip chart-tooltip-axis")
      .style("right", width + margin.right + "px");

  // Draw tooltip on Dual-Y axis
  const drawChartTooltipDualYAxis = (drawingContainerElementId, uniqueKey, id, margin, width) =>
    d3
      .select("#" + drawingContainerElementId)
      .append("div")
      .attr("id", id + "" + uniqueKey)
      .attr("class", "chart-tooltip chart-tooltip-axis")
      .style("left", width + margin.left + "px");

  // Position tooltip on axis
  const positionChartTooltipAxis = (axisTooltip, value, top, chartType = null) =>
    axisTooltip
      .html(mathManager.toUSANumberFormat(truncateDecimalPointsByChartType(value, chartType)))
      .style("display", "block")
      .style("top", top + "px");

  // Draw focus line
  const drawFocusLine = (focus, uniqueKey, id) =>
    focus
      .append("line")
      .attr("id", id + "" + uniqueKey)
      .attr("class", "line focus-line");

  // Draw focus circle
  const drawFocusCircle = (focus, uniqueKey, id, className) =>
    focus
      .append("circle")
      .attr("id", id + "" + uniqueKey)
      .attr("r", CHART_FOCUS_POINT_RADIUS)
      .attr("class", className);

  // Position X axis focus line
  const positionXAxisFocusLine = (focus, uniqueKey, id, hoveredXValue, height) =>
    focus
      .select("#" + id + "" + uniqueKey)
      .style("display", "block")
      .attr("x1", hoveredXValue)
      .attr("y1", height)
      .attr("x2", hoveredXValue)
      .attr("y2", 0);

  // Position Y axis focus line
  const positionYAxisFocusLine = (focus, uniqueKey, id, x1, y1, x2, y2) =>
    focus
      .select("#" + id + "" + uniqueKey)
      .style("display", "block")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2);

  // Position focus circle
  const positionFocusCircle = (focus, uniqueKey, id, cx, cy) =>
    focus
      .select("#" + id + "" + uniqueKey)
      .style("display", "block")
      .attr("cx", cx)
      .attr("cy", cy);

  // Get min and max from data range
  const getMinMaxValues = (data, properties) => {
    if (typeof properties === "string") {
      return [
        d3.min(data, function(d) {
          return d[properties] || undefined;
        }),
        d3.max(data, function(d) {
          return d[properties] || undefined;
        })
      ];
    } else {
      return [
        d3.min(data, function(d) {
          // pick: pick specific object keys, values: get values, pull: skip 0 values
          return Math.min(..._.pull(_.values(_.pick(d, properties)), 0));
        }),
        d3.max(data, function(d) {
          // pick: pick specific object keys, values: get values, pull: skip 0 values
          return Math.max(..._.pull(_.values(_.pick(d, properties)), 0));
        })
      ];
    }
  };

  // Revisit domain min and max specially for mirroring
  const recalculateDomainForMirroring = (domain) => {
    let max = _.max([domain[0] < 0 ? -domain[0] : domain[0], domain[1]]);
    return [-max, max];
  };

  // Explicitly generate the set of values for X axis
  // (specifically to avoid overlapping of X axis tick labels)
  const generateXAxisTickValues = (chartData, zoneOffset, granularity, width) => {
    const pixelPerTickLabel = 75; // width of each tick label
    let chartDataLength = chartData.length;
    let tickValues = [];
    let firstDate = datetimeManager.getMomentObject(chartData[0][API_KEY_TIMESTAMP], zoneOffset);
    let lastDate = datetimeManager.getMomentObject(chartData[chartDataLength - 1][API_KEY_TIMESTAMP], zoneOffset);

    let incrementalDuration = undefined;
    // Duration between consecutive tick values, based on selected granularity
    if (granularity === GRANULARITY_YEAR) {
      incrementalDuration = "years";
    } else if (granularity === GRANULARITY_MONTH || granularity === GRANULARITY_QUARTER) {
      incrementalDuration = "months";
    } else if (granularity === GRANULARITY_WEEK) {
      incrementalDuration = "weeks";
    } else {
      incrementalDuration = "days";
    }

    let startEndDateDiff = lastDate.diff(firstDate, incrementalDuration);

    // calculation of duration after which next tick should appear according to selected granularity
    let incrementalStep = Math.floor(
      (granularity === GRANULARITY_QUARTER ? Math.floor(startEndDateDiff / 3) : startEndDateDiff) / Math.floor(width / pixelPerTickLabel)
    );

    incrementalStep = incrementalStep > 1 ? incrementalStep : 1;

    // loop through the range between start and end date
    while (lastDate.diff(firstDate, "days") >= 0) {
      // push 1st date
      tickValues.push(firstDate);
      // push TimeStamp which appears after every incremental duration
      firstDate = firstDate.clone().add(granularity === GRANULARITY_QUARTER ? incrementalStep * 3 : incrementalStep, incrementalDuration);
    }

    return tickValues;
  };

  // Least Squares Regression for Cost To Trade trend lines
  // https://www.mathsisfun.com/data/least-squares-regression.html
  // http://bl.ocks.org/tommyogden/f416a5c27971908845a9
  const leastSquares = (xSeries, ySeries) => {
    let reduceSumFunc = function(prev, cur) {
      return prev + cur;
    };

    let xBar = xSeries.reduce(reduceSumFunc) * 1.0 / xSeries.length;
    let yBar = ySeries.reduce(reduceSumFunc) * 1.0 / ySeries.length;

    let xValue = xSeries
      .map(function(d) {
        return Math.pow(d - xBar, 2);
      })
      .reduce(reduceSumFunc);

    let yValue = ySeries
      .map(function(d) {
        return Math.pow(d - yBar, 2);
      })
      .reduce(reduceSumFunc);

    let xyValue = xSeries
      .map(function(d, i) {
        return (d - xBar) * (ySeries[i] - yBar);
      })
      .reduce(reduceSumFunc);

    let slope = xyValue / xValue;
    let intercept = yBar - xBar * slope;
    let rSquare = Math.pow(xyValue, 2) / (xValue * yValue);

    return [slope, intercept, rSquare];
  };

  // Highlight role shift on chart with alternate background colors
  const highlightRoleShift = (chartData, zoneOffset, chartApiKeys, chartSvg, chartHeight, calcRoleWidth) => {
    let contracts = _.groupBy(chartData, 'CT');
    let highlightClass = "chart-bg-dark";
    let startingPoint = 0;
    let MaxTS = null;
    for (var key in contracts) {
      // Switch between light and dark background color class
      highlightClass = highlightClass === "chart-bg-dark" ? "chart-bg-light" : "chart-bg-dark";

      // Calculate width for individual role
      MaxTS = _.maxBy(contracts[key], 'TS');
      let roleWidth = calcRoleWidth(datetimeManager.getMomentObject(MaxTS[chartApiKeys.timestamp], zoneOffset));

      // Create rectangle element and append it to the main svg element
      chartSvg
      .append("rect")
      .attr("class", highlightClass)
      .attr("width", roleWidth - startingPoint - 1)
      .attr("x", startingPoint === 0 ? 1 : startingPoint)
      .attr("height", chartHeight);

      // Set starting point to the role width of the previous rect element, and use it as a starting point for next rect element
      startingPoint = roleWidth;
    }
  };

  // Truncate decimal points by chart type
  // 'FixDecimalPointsTo' is the property of CHARTS object to be configured against each chart type
  const truncateDecimalPointsByChartType = (decimalNumber, chartType) => {
    const selectedChartConfig = _.find(CHARTS, {Code : chartType});
    return selectedChartConfig ? _.round(decimalNumber, selectedChartConfig.FixDecimalPointsTo) : decimalNumber;
  };

  return {
    getDrawingContainerElementId,
    destroyDrawing,
    getPercentageChange,
    createSvg,
    createXAxisTimeScale,
    createYAxisLinearScale,
    createDualYAxisLinearScale,
    drawXAxis,
    drawYAxis,
    drawDualYAxis,
    drawXAxisLabel,
    drawYAxisLabel,
    drawYDualAxisLabel,
    drawChartDataTooltip,
    drawChartTooltipYAxis,
    drawChartTooltipDualYAxis,
    drawFocusLine,
    drawFocusCircle,
    positionXAxisFocusLine,
    positionYAxisFocusLine,
    positionChartTooltipAxis,
    positionChartDataTooltip,
    positionFocusCircle,
    getMinMaxValues,
    getXAxisDateFormat,
    recalculateDomainForMirroring,
    generateXAxisTickValues,
    leastSquares,
    highlightRoleShift,
    truncateDecimalPointsByChartType
  };
})(document);

export default service;



// WEBPACK FOOTER //
// ./src/services/renderers/common.js